<template>
    <div class="multi-step-filter"
        :class="{
            'el--no-search': hideSearch,
            'el--no-breadcrumbs': hideBreadcrumbs && !uiShowClearSelection && !$slots.actions
        }"
        :style="{height: uiHeight !== false ? `${uiHeight}rem` : '100%'}">
        <div class="multi-step-filter-header c-el-panel-bg">
            <div v-if="!hideBreadcrumbs || uiShowClearSelection || $slots.actions" class="f fa-c sm-b-s">
                <!-- Back navigation on top -->
                <BreadcrumbsList v-if="!hideBreadcrumbs"
                    class="sp-xxs fg"
                    :list="uiBreadcrumbs"
                    @actSelect="onBreadcrumbClick" />
                <span v-else class="fg"></span>
                <!-- Clears the whole selection, not just the current level -->
                <ButtonV2 v-if="uiShowClearSelection"
                    class="sm-l-s"
                    label="mead.labels.clear"
                    link
                    :disabled="!engine.canClearSelection()"
                    @click.prevent.stop="onClearSelection" />
                <slot name="actions"></slot>
            </div>
            <!-- Search Input -->
            <div v-if="!hideSearch" class="sm-b-s dw-10">
                <FormSearch v-model="qstring"
                    :placeholder="uiSearchPlaceholder"
                    :debounce="false" />
            </div>
        </div>
        <div class="multi-step-filter-items">
            <!-- "No matches found" message if no matching items -->
            <div v-if="qstring.length >= 3 && uiItems.length === 0" class="sp-s el--not-found">
                <p>
                    <strong>{{ 'mead.no_matches_found' | athT }}</strong>
                </p>
                <p>{{ 'mead.check_for_typos' | athT }}</p>
            </div>
            <!-- Items -->
            <div v-else>
                <!-- Select All will only show up in following conditions are all met
                     - multi mode
                     - min and max are 0
                     - No query
                     - At least one value to select (only leaf values when allowGroupSelection=false)  -->
                <MultiStepFilterSelectAll :qstring="qstring"
                    :items="uiItems"
                    :engine="engine"
                    @onCheck="onCheck" />
                <MultiStepFilterItem v-for="item in uiItems"
                    :item="item"
                    :key="item.value"
                    :qstring="qstring"
                    :showBadgeCounts="showBadgeCounts"
                    :engine="engine"
                    @onItemSelected="onItemSelected"
                    @onOpenGroup="onOpenGroup"
                    @onCheck="onCheck" />
            </div>
        </div>
    </div>
</template>
<script>
import BreadcrumbsList from '../../atoms/BreadcrumbsList.vue';
import ButtonV2 from '../../atoms/ButtonV2.vue';
import FormSearch from '../../atoms/forms/FormSearch.vue';
import MultiStepFilterItem from './MultiStepFilterItem.vue';
import MultiStepFilterSelectAll from './MultiStepFilterSelectAll.vue';

export default {
    name: 'mead-multi-step-filter',
    props: {
        /**
         * Title of the first breadcrumb
         */
        title: {type: String, default: 'mead.options' },
        /**
         * Hide the search input above the list. Useful for short, fixed lists where searching
         * adds nothing.
         */
        hideSearch: {type: Boolean, default: false},
        /**
         * Hide the breadcrumb trail above the list. Only use this with flat lists: the breadcrumbs
         * are the only way to navigate back out of a group, so it should not be used with grouped
         * data (including when startingItem opens the filter inside a group). Does not affect the
         * `actions` slot, which stays available either way.
         */
        hideBreadcrumbs: {type: Boolean, default: false},
        /**
         * Show a "Clear" action in the header that removes the entire selection across all
         * levels. Not shown in no-select mode or when min is greater than 0, and disabled while
         * nothing is selected.
         */
        showClearSelection: {type: Boolean, default: false},
        /**
         * Public key if using this in a popover
         */
        pubkey: {type: String, default: '' },
        /**
         * Array of MultiStepFilterItem's
         */
        items: {type: Array, default: () => []},
        /**
         * Array of values representing the currently selected items. Does not include
         * group values unless includeGroups is true.
         */
        selectedItems: {type: Array, default: () => []},
        /**
         * Items that are not allowed to be selected because they are restricted.
         * Only for single selection mode
         */
        restrictedItems: {type: Array, default: () => []},
        /**
         * Max amount of items that can be selected. 0 is no limit
         */
        max: {type: Number, default: 0},
        /**
         * Minimum amount of items that can be selected. 0 is no limit
         */
        min: {type: Number, default: 0},
        /**
         * If groups should be included in selection
         */
        includeGroups: {type: Boolean, default: true},
        /**
         * Show Checkmarks on groups or allow clicking groups. If all groups should be
         * clicked set this to true, otherwise use the always_selectable prop for filter item
         */
        allowGroupSelection: {type: Boolean, default: false},
        /**
         * Selection mode
         */
        mode: {type: String, default: () => MeadCommon.MUTLI_STEP_FILTER_MODES.NO_SELECT},
        /**
         * Show count of selected children on badges
         */
        showBadgeCounts: {type: Boolean, default: true},
        /**
         * If true, parent groups remain checked even when only some children are selected
         */
        keepPartialGroups: {type: Boolean, default: false},
        /**
         * if true, selection will only include the item selected, not the children of a group.
         */
        isolateSelection: {type: Boolean, default: false},
        /**
         * Item to open the selector too by default
         */
        startingItem: {type: String, default: undefined},
        /**
         * Height of the component
         */
        uiHeight: {type: [String, Number, Boolean], default: '40'},
    },
    components: {MultiStepFilterItem, BreadcrumbsList, ButtonV2, FormSearch, MultiStepFilterSelectAll},
    data() {
        const engine = new MeadCommon.MultiFilterEngine()
            .setItems(this.items)
            .setSelectedItems(this.selectedItems)
            .setRestrictedItems(this.restrictedItems)
            .setMax(this.max)
            .setMin(this.min)
            .setIncludeGroups(this.includeGroups)
            .setAllowGroupSelection(this.allowGroupSelection)
            .setMode(this.mode)
            .setKeepPartialGroups(this.keepPartialGroups)
            .setIsolateSelection(this.isolateSelection)

        return {
            qstring: '',
            currentLayer: 0,
            layers: [{
                label: this.title,
                children: this.items,
            }],
            engine,
        };
    },
    methods: {
        onBreadcrumbClick: function (obj) {
            this.currentLayer = obj.index;
            this.layers = this.layers.slice(0, obj.index + 1);
            const currentLayerData = this.layers[this.currentLayer];

            // Get the item that represents this breadcrumb level
            // For non-root levels, we can get it from the first child's parent
            let breadcrumbItem = null;
            if (obj.index > 0 && currentLayerData.children && currentLayerData.children.length > 0) {
                // The first child's parent is the item this breadcrumb represents
                breadcrumbItem = currentLayerData.children[0].parent;
            }

            this.$emit('onBreadcrumbClick', {
                index: obj.index,
                layer: currentLayerData,
                item: breadcrumbItem  // null for root, MultiStepFilterItem for groups
            });
            MeadCommon.Flash.publish(`${this.pubkey}:breadcrumb_clicked`, {
                index: obj.index,
                layer: currentLayerData,
                item: breadcrumbItem
            });
        },
        onItemSelected: function (item) {
            if (this.mode === MeadCommon.MUTLI_STEP_FILTER_MODES.SINGLE) {
                const selectedItems = this.engine.isSelected(item) ? [] : [item.value];
                this.engine.setSelectedItems(selectedItems);
                this.$emit('onItemSelected', this.engine.selected, item);
            } else if (this.mode === MeadCommon.MUTLI_STEP_FILTER_MODES.MULTI) {
                this.engine.setSelectedItems(item.allValues);
                this.$emit('onItemSelected', this.engine.selected);
            }
            MeadCommon.Flash.publish(`${this.pubkey}:item_selected`, this.engine.selected);
        },
        onOpenGroup: function (item) {
            this.layers = [{
                label: this.title,
                children: this.items,
                sort_dir: MeadCommon.MULTI_STEP_FILTER_SORT.ASC
            }, ...(item?.breadcrumbs || [])]
            this.currentLayer = this.layers.length - 1;

            // Emit event to parent so they can handle the group navigation
            // In SINGLE mode, this allows filtering by the opened group
            if (this.mode === MeadCommon.MUTLI_STEP_FILTER_MODES.SINGLE) {
                this.$emit('onGroupOpened', item);
                MeadCommon.Flash.publish(`${this.pubkey}:group_opened`, item);
            }
        },
        onCheck: function (selection) {
            const selection_output = this.engine.updateSelection(selection);
            this.$emit('onCheck', selection_output);
            MeadCommon.Flash.publish(`${this.pubkey}:check_changed`, selection_output);
        },
        onClearSelection: function () {
            if (this.mode === MeadCommon.MUTLI_STEP_FILTER_MODES.SINGLE) {
                this.engine.setSelectedItems([]);
                this.$emit('onItemSelected', this.engine.selected, null);
                MeadCommon.Flash.publish(`${this.pubkey}:item_selected`, this.engine.selected);
            } else {
                this.onCheck([]);
            }
        }
    },
    computed: {
        uiShowClearSelection: function () {
            return this.showClearSelection && this.mode !== MeadCommon.MUTLI_STEP_FILTER_MODES.NO_SELECT && this.min === 0;
        },
        uiSearchPlaceholder: function () {
            return MeadCommon.I18N.go('mead.search_x', {
                value: MeadCommon.I18N.go(this.layers[this.currentLayer].label)
            });
        },
        uiItems: function () {
            let items = [];
            // If searching, just return search results
            if (this.qstring.length >= 3) {
                const matches = [];

                for (const item of this.layers[this.currentLayer].children) {
                    matches.push(...item.search(this.qstring));
                }
                items = matches;
            } else {
                items = this.layers[this.currentLayer].children;
            }

            return MeadCommon.MultiStepFilterItem.sortWithPinned(items, this.layers[this.currentLayer].sort_dir);
        },
        uiBreadcrumbs: function () {
            const breadcrumbs = [];
            for (let i = 0; i < this.layers.length; i++){
                breadcrumbs.push({
                    name: this.layers[i].label,
                    data: {
                        index: i
                    }
                })
            }
            return breadcrumbs;
        },
    },
    watch: {
        items: function () { this.engine.setItems(this.items, false); },
        selectedItems: function () { this.engine.setSelectedItems(this.selectedItems); },
        restrictedItems: function () { this.engine.setRestrictedItems(this.restrictedItems); },
        max: function () { this.engine.setMax(this.max); },
        min: function () { this.engine.setMin(this.min); },
        includeGroups: function () { this.engine.setIncludeGroups(this.includeGroups, false); },
        allowGroupSelection: function () { this.engine.setAllowGroupSelection(this.allowGroupSelection); },
        mode: function () { this.engine.setMode(this.mode); },
        keepPartialGroups: function () { this.engine.setKeepPartialGroups(this.keepPartialGroups, false); },
        isolateSelection: function () { this.engine.setIsolateSelection(this.isolateSelection); },
    },
    created: function () {
        // If there is a starting item, open the tree at that item
        if (this.startingItem !== undefined) {
            const findItemWithValue = function (list, value, items=[]) {
                for (const item of list) {
                    if (item.value === value) items.push(item);
                    if (item.isGroup && items.length === 0) {
                        findItemWithValue(item.children, value, items);
                    }
                }
            }
            const result = [];
            findItemWithValue(this.items, this.startingItem, result);
            if (result.length > 0) this.onOpenGroup(result[0].parent);
        } else if (this.items.length === 1) {
            this.onOpenGroup(this.items[0]);
        }
    }
};
</script>
<style lang="scss" scoped>
@import '../../../../sass/_variables.scss';

.multi-step-filter {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    position: relative;

    .multi-step-filter-header {
        position: absolute;
        width: 100%;
    }

    // The header is absolutely positioned, so the list is offset by its height:
    // breadcrumb row (5.5rem) + search row (4rem)
    .multi-step-filter-items {
        margin-top: 9.5rem;
    }

    &.el--no-search .multi-step-filter-items {
        margin-top: 5.5rem;
    }

    &.el--no-breadcrumbs .multi-step-filter-items {
        margin-top: 4rem;
    }

    &.el--no-search.el--no-breadcrumbs .multi-step-filter-items {
        margin-top: 0;
    }

    .el--not-found {
        color: var(--c-txt-light);
    }

    .multi-step-filter-items {
        flex: 1;
        overflow: auto;
    }
}
</style>
